import { User } from '@bechtel/ht-types';
import { some } from 'lodash';
import React, { PropsWithChildren, useContext, useState } from 'react';
import { AppUserProps, AppUserProviderProps } from './types';

// check user has one specific permission
function hasPermission(permission: string, user: User | null) {
  if (!permission || !user) return false;
  const { permissionScopes } = user;
  if (!permissionScopes || permissionScopes.length === 0) return false;
  return some(permissionScopes, (permissionScopes) => permissionScopes.permission?.name === permission);
}
// check user has all permission specified in the permission list
export function hasPermissions(permissionList: string[], user: User | null) {
  if (!permissionList || !user || permissionList.length <= 0) return false;
  const { permissionScopes } = user;
  if (!permissionScopes || permissionScopes.length === 0) return false;
  return permissionList.every((inputPermission) => {
    return hasPermission(inputPermission, user);
  });
}

const UserStateContext: React.Context<User | null> = React.createContext<User | null>(null);
const UserUpdaterContext: React.Context<React.Dispatch<React.SetStateAction<User | null>>> =
  //react create context accepts null, bug with type definitions
  //@ts-ignore react create context accepts null, bug with type definitions
  React.createContext<React.Dispatch<React.SetStateAction<User | null>>>(null);

function useAppUser(): AppUserProps {
  const appUser = useContext(UserStateContext);
  if (appUser === undefined) {
    throw new Error(`useAppUser must be used within a AppUserProvider`);
  }
  const userHasPermission = React.useCallback((permission: string) => hasPermission(permission, appUser), [appUser]);
  const userHasPermissions = React.useCallback(
    (permissions: string[]) => hasPermissions(permissions, appUser),
    [appUser]
  );

  return {
    appUser,
    hasPermission: userHasPermission,
    hasPermissions: userHasPermissions,
  };
}

function useAppUserUpdater() {
  const setAppUser = useContext(UserUpdaterContext);
  if (typeof setAppUser === undefined) {
    throw new Error(`useAppUserUpdater must be used within a AppUserProvider`);
  }
  const userAuthorized = React.useCallback((authorizedUser: User) => setAppUser(authorizedUser), [setAppUser]);
  const userNotAuthorized = React.useCallback(() => setAppUser(null), [setAppUser]);
  return { userAuthorized, userNotAuthorized };
}

function AppUserProvider(props: PropsWithChildren<AppUserProviderProps>) {
  const [appUser, setAppUser] = useState<User | null>(props && props.userAccount ? props.userAccount : null);

  return (
    <UserStateContext.Provider value={appUser}>
      <UserUpdaterContext.Provider value={setAppUser}>{props.children}</UserUpdaterContext.Provider>
    </UserStateContext.Provider>
  );
}

export { AppUserProvider, useAppUser, useAppUserUpdater };
